import React, { useState, useEffect } from "react";
import { Input, Textarea } from "../../../ui";
import AddNewCard from "../../../payment/stripe/AddNewCard";
import ReceiptNumberWarning from "../../../payment/ReceiptNumberWarning";
import type { LeadBillingDecision } from "../../../../hooks/useLeadBillingPrompt";
import { generateReceiptNumber } from "../../../../utils/receiptNumber";
import { useTheme } from "../../../../context/ThemeContext";
import {
  getModalStyles,
  getPrimaryText,
  getSecondaryText,
  getInputStyles
} from "../../../../utils/themeUtils";

type SelectPaymentMethodModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    method: "cash" | "card" | "cheque" | "account_credit";
    amount: number;
    note: any;
    receiptNumber: any;
    useSavedCard: boolean;
    checkDetails?: {
      checkNumber: string;
      bankName: string;
      checkDate: string;
    };
    // Return `false` to signal the payment failed and keep the modal open
    // (with the entered data intact). Any other value — including void, for
    // legacy consumers — closes the modal as before.
  }) => void | boolean | Promise<void | boolean>;
  invoice: any;
  savedCardLast4?: string;
  fetch: any;
  /**
   * What staff already chose for a lead earlier in this same attempt, handed
   * on to the card form so it does not ask a second time.
   */
  leadBilling?: LeadBillingDecision | null;
};

const SelectPaymentMethodModal: React.FC<SelectPaymentMethodModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  invoice,
  savedCardLast4,
  fetch,
  leadBilling,
}) => {
  const { isDarkMode } = useTheme();
  const [method, setMethod] = useState<
    "cash" | "card" | "cheque" | "account_credit"
  >(savedCardLast4 ? "card" : "cash");
  // A partly paid invoice starts at what is still owed (the server refuses
  // more than that), anything else at its full amount.
  const startingAmount = (inv: any) => Number(inv?.remaining_owed ?? inv?.amount ?? 0) || 0;
  const [amount, setAmount] = useState(startingAmount(invoice));
  const [useSavedCard, setUseSavedCard] = useState(true);
  const [settings, setSettings] = useState<any>(null);

  // check fields
  const [checkNumber, setCheckNumber] = useState("");
  const [bankName, setBankName] = useState("");
  const [checkDate, setCheckDate] = useState("");
  // note field
  const [note, setNote] = useState("");

  const [receiptNumber, setReceiptNumber] = useState();
  // In-flight guard so the modal can't be double-submitted and only closes
  // once the payment actually succeeds (a failed attempt keeps the form open
  // with its data intact instead of silently closing).
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handel = async () => {
      const recipNumber: any = await generateReceiptNumber();
      if (recipNumber) {
        setReceiptNumber(recipNumber);
      }
    };
    if (isOpen) {
      handel();
    }
  }, [isOpen === true]);


  useEffect(() => {
    if (invoice?.amount) setAmount(startingAmount(invoice));
  }, [invoice]);

  // Load settings from localStorage
  useEffect(() => {
    const storedSettings = localStorage.getItem("settings");
    if (storedSettings) {
      try {
        setSettings(JSON.parse(storedSettings));
      } catch (e) {
        // ignore parse errors
      }
    }
  }, []);

  const requireCheckNumber = settings?.require_check_number !== false;

  if (!isOpen) return null;

  const resetFields = () => {
    setBankName("");
    setAmount(startingAmount(invoice));
    setCheckDate("");
    setCheckNumber("");
    setNote("");
  };

  const handleConfirm = async () => {
    if (submitting) return;
    if (method === "cheque" && requireCheckNumber && !checkNumber.trim()) {
      alert("Check number is required.");
      return;
    }

    const payload: any = {
      method,
      amount,
      useSavedCard: method === "cash" ? false : useSavedCard,
      note,
      receiptNumber,
    };

    if (method === "cheque") {
      payload.checkDetails = {
        checkNumber,
        bankName,
        checkDate,
      };
    }

    setSubmitting(true);
    try {
      // Close (and clear the form) unless the handler explicitly reports
      // failure by returning false — in that case keep the modal open so staff
      // can correct and retry without re-entering everything, and without a
      // second submission racing the first.
      const result = await onSubmit(payload);
      if (result !== false) {
        onClose();
        resetFields();
      }
    } finally {
      setSubmitting(false);
    }
  };
  const handelClose = () => {
    if (submitting) return;
    onClose();
    resetFields();
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className={`rounded p-6 shadow-lg w-[90%] max-w-md max-h-[90vh] overflow-auto ${getModalStyles(isDarkMode)}`}>
        <h3 className={`text-lg font-semibold mb-2 ${getPrimaryText(isDarkMode)}`}>Select Payment Method</h3>

        <div className="space-y-3">
          {/* Card */}
          <div className="flex items-center space-x-3">
            <input id="paymethod-card"
              type="radio"
              name="method"
              value="card"
              checked={method === "card"}
              onChange={() => setMethod("card")}
            />
            <label htmlFor="paymethod-card" className={getPrimaryText(isDarkMode)}>Card</label>
          </div>

          {/* Cash */}
          <div className="flex items-center space-x-3">
            <input id="paymethod-cash"
              type="radio"
              name="method"
              value="cash"
              checked={method === "cash"}
              onChange={() => setMethod("cash")}
            />
            <label htmlFor="paymethod-cash" className={getPrimaryText(isDarkMode)}>Cash</label>
          </div>

          {/* Cheque */}
          <div className="flex items-center space-x-3">
            <input id="paymethod-cheque"
              type="radio"
              name="method"
              value="cheque"
              checked={method === "cheque"}
              onChange={() => setMethod("cheque")}
            />
            <label htmlFor="paymethod-cheque" className={getPrimaryText(isDarkMode)}>Check</label>
          </div>

          {/* Account Credit */}
          <div className="flex items-center space-x-3">
            <input id="paymethod-account-credit"
              type="radio"
              name="method"
              value="account_credit"
              checked={method === "account_credit"}
              onChange={() => setMethod("account_credit")}
            />
            <label htmlFor="paymethod-account-credit" className={getPrimaryText(isDarkMode)}>Account Credit</label>
          </div>

          {/* Amount */}
          <div>
            <Input
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value))}
            />
          </div>

          {/* Receipt # — always shown so staff can record a paper-receipt
              cross-reference even on card / account-credit payments. */}
          <div>
            <Input
              label="Receipt #"
              type="text"
              value={receiptNumber}
              onChange={(e) => setReceiptNumber(e.target.value as any)}
              placeholder={
                method === "card" || method === "account_credit"
                  ? "Optional — for cross-reference with a paper receipt"
                  : ""
              }
            />
            {/* A warning only: a reused number never stops the payment. */}
            <ReceiptNumberWarning membershipId={invoice?.membership_id} receiptNumber={receiptNumber} />
          </div>

          {/* Note */}
          <div>
            <Textarea
              label="Note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder='Optional — e.g. "mom paid", reference number, anything you want recorded'
            />
          </div>

          {/* Saved card or Add new card */}
          {method === "card" && (
            <div className="mt-2">
              {savedCardLast4 && (
                <label className={`inline-flex items-center ${getPrimaryText(isDarkMode)}`}>
                  <input
                    type="checkbox"
                    checked={useSavedCard}
                    onChange={() => setUseSavedCard(!useSavedCard)}
                  />
                  <span className="ml-2">
                    Use saved card (•••• {savedCardLast4})
                  </span>
                </label>
              )}
              {/* Show AddNewCard when no saved card exists OR when user unchecks "use saved card" */}
              {(!savedCardLast4 || !useSavedCard) && (
                <div className={savedCardLast4 ? "mt-3" : ""}>
                  {!savedCardLast4 && (
                    <p className={`text-sm mb-2 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>No saved card found.</p>
                  )}
                  {/* Billing Information Section */}
                  <AddNewCard
                    amount={amount}
                    invoice={invoice}
                    onClose={onClose}
                    fetch={fetch}
                    leadBilling={leadBilling}
                  />
                </div>
              )}
            </div>
          )}

          {/* Cheque details */}
          {method === "cheque" && (
            <div className="space-y-2 mt-3">
              <div>
                <label htmlFor="paymethod-check-number" className={`block text-sm font-medium mb-1 ${getSecondaryText(isDarkMode)}`}>
                  Check Number{requireCheckNumber && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input id="paymethod-check-number"
                  type="text"
                  className={`w-full px-3 py-2 rounded ${getInputStyles(isDarkMode)}`}
                  value={checkNumber}
                  onChange={(e) => setCheckNumber(e.target.value)}
                />
              </div>

              {/* <div>
                <Input
                  label="Bank Name"
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                />
              </div> */}

              <div>
                <Input
                  label="Check Date"
                  type="date" autoComplete="off"
                  value={checkDate}
                  onChange={(e) => setCheckDate(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <button className={`px-4 py-2 rounded border transition-colors disabled:opacity-50 ${isDarkMode ? 'border-gray-800 text-gray-300 hover:bg-gray-700' : 'border-gray-600 text-gray-700 hover:bg-gray-50'}`} onClick={handelClose} disabled={submitting}>
            Cancel
          </button>
          {/* Show Confirm button for: cash, cheque, account_credit, or card with saved card checked */}
          {(method === "cash" ||
            method === "cheque" ||
            method === "account_credit" ||
            (method === "card" && savedCardLast4 && useSavedCard)) && (
            <button
              className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              onClick={handleConfirm}
              disabled={
                submitting ||
                (method === "cheque" && requireCheckNumber && !checkNumber.trim())
              }
            >
              {submitting ? "Processing…" : "Confirm"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectPaymentMethodModal;
